import { useCallback, useState } from 'react';
import type { DealPerson } from './deal-kit';
import { Avatar } from '@forecastify/ui';
import { initialsOf } from './deal-kit';
import type { DealRecord } from './use-deal-record';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D5 · what the overview's cards share.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * One write per card with its own refusal slot (`state-rules.md` §2: a rejection sits beside the
 * editor that caused it), a borderless text box for the rail, and a person's chip.
 */

/** A card's writes: `run` resolves true on success; a refusal lands in `error` until the next. */
export function useCardWrite(record: DealRecord) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = useCallback(
    async (path: string, method: string, body?: unknown): Promise<boolean> => {
      setBusy(true);
      const refusal = await record.write(path, method, body);
      setBusy(false);
      setError(refusal);
      return refusal === null;
    },
    [record],
  );
  return { error, busy, run };
}

export type CardWrite = ReturnType<typeof useCardWrite>;

/** The refusal under a card's controls — nothing when there is none. */
export function Refusal({ error }: { readonly error: string | null }) {
  if (error === null) return null;
  return (
    <p className="text-destructive px-1 text-[11.5px]" role="alert">
      {error}
    </p>
  );
}

/**
 * A rail value somebody types — a campaign, a competitor, the tags. `RailMoney`'s contract for
 * text: commits on Enter or blur, `Escape` reverts, and a live update reaches the box only while
 * nobody is typing in it.
 */
export function RailText({
  value,
  onCommit,
  label,
  placeholder = '–',
  maxLength,
}: {
  readonly value: string;
  readonly onCommit: (value: string) => void;
  readonly label: string;
  readonly placeholder?: string;
  readonly maxLength?: number;
}) {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);
  if (!editing && draft !== value) setDraft(value);

  const commit = (): void => {
    setEditing(false);
    if (draft.trim() !== value.trim()) onCommit(draft.trim());
  };

  return (
    <input
      value={draft}
      aria-label={label}
      placeholder={placeholder}
      maxLength={maxLength}
      className="hover:border-input focus:border-input focus:bg-background h-[30px] w-full min-w-0 rounded-md border border-transparent bg-transparent px-2 text-[12.5px] outline-none"
      onChange={(event) => {
        setEditing(true);
        setDraft(event.target.value);
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') {
          setDraft(value);
          setEditing(false);
          event.currentTarget.blur();
        }
      }}
    />
  );
}

/** A person's face, from the member feed when they are in it — initials otherwise. */
export function PersonAvatar({
  name,
  memberId,
  members,
  size = 20,
}: {
  readonly name: string;
  readonly memberId: string | null;
  readonly members: readonly DealPerson[];
  readonly size?: number;
}) {
  const seed = members.find((member) => member.id === memberId)?.avatarSeed ?? null;
  return (
    <Avatar
      fallback={initialsOf(name)}
      title={name}
      size={size}
      {...(seed === null ? {} : { seed })}
    />
  );
}

/** A `YYYY-MM-DD` out of an instant the server wrote at noon UTC for a calendar date. */
export function dueDateOf(iso: string): string {
  return iso.slice(0, 10);
}
